import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import axios from 'axios';

const WEB_HEADERS = {
  'X-Web-Token': process.env.REACT_APP_WEB_TOKEN || ''
};

const BreachCheck = () => {
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState('email');
  const [breachData, setBreachData] = useState(null);
  const [lawsuitData, setLawsuitData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resultsTab, setResultsTab] = useState(0);

  const handleSearch = async () => {
    if (!identifier.trim()) {
      setError('Please enter an identifier to check');
      return;
    }

    setIsLoading(true);
    setError(null);
    setBreachData(null);
    setLawsuitData(null);
    setResultsTab(0);

    try {
      const breachResponse = await axios.post('/api/check-breaches', {
        identifier: identifier.trim(),
        identifierType: identifierType,
      }, { headers: WEB_HEADERS });

      const breaches = breachResponse.data.breaches || [];
      setBreachData(breachResponse.data);
      setIsLoading(false);

      if (breaches.length > 0) {
        setLawsuitData({ loading: true });
        
        axios.post('/api/find-lawsuits', { breaches: breaches }, { headers: WEB_HEADERS })
          .then(lawsuitResponse => {
            setLawsuitData(lawsuitResponse.data);
          })
          .catch(lawsuitErr => {
            console.error('Error fetching lawsuits:', lawsuitErr);
            setLawsuitData({ 
              lawsuits: [],
              error: 'Failed to load lawsuit data. Please try again.'
            });
          });
      } else {
        setLawsuitData({ lawsuits: [] });
      }
    } catch (err) {
      console.error('Error during search:', err);
      setError(
        err.response?.data?.error || 
        'An error occurred while checking breaches. Please try again.'
      );
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !isLoading) {
      handleSearch();
    }
  };

  const handleResultsTabChange = (event, newValue) => {
    setResultsTab(newValue);
  };

  const showUpgradeMessage = identifierType !== 'email';

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h2" component="h1" fontWeight="700" gutterBottom>
          PII Breach Monitor
        </Typography>
        <Typography variant="h5" color="text.secondary">
          Check if your personal information has been compromised
        </Typography>
      </Box>

      {/* Legal Disclaimer */}
      <Alert severity="warning" sx={{ mb: 4 }}>
        <strong>Legal Disclaimer:</strong> This tool is provided for informational purposes only and does not constitute legal advice. 
        The results should not be considered a substitute for professional legal counsel. If you believe your personal information 
        has been compromised or you are considering legal action, please consult with a qualified attorney who specializes in 
        data breach and privacy law.
      </Alert>

      {/* Search Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Check Your Breach Status
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your email to see if it has been exposed in known data breaches
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="identifier-type-label">Type</InputLabel>
            <Select
              labelId="identifier-type-label"
              value={identifierType}
              label="Type"
              onChange={(e) => setIdentifierType(e.target.value)}
              disabled={isLoading}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="username">Username</MenuItem>
              <MenuItem value="phone">Phone Number</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Your Identifier"
            variant="outlined"
            fullWidth
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            placeholder={
              identifierType === 'email' ? 'user@example.com' :
              identifierType === 'username' ? 'username123' :
              '+1234567890'
            }
            sx={{ flexGrow: 1 }}
          />

          <Button
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            sx={{ height: 56, minWidth: 160 }}
          >
            {isLoading ? 'Checking...' : 'Check Breaches'}
          </Button>
        </Box>

        {showUpgradeMessage && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Free Tier Limitation:</strong> Only email searches are supported on the free tier.
            Upgrade to premium to check usernames and phone numbers.
          </Alert>
        )}

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon fontSize="small" color="primary" />
            <strong>Your Privacy:</strong> We check your identifier against publicly known data breaches. 
            We do NOT search the dark web. We will NEVER store or sell your personal information.
          </Typography>
        </Box>
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Section */}
      {(breachData || lawsuitData) && !isLoading && (
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={resultsTab}
            onChange={handleResultsTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}
          >
            <Tab 
              label="Breach Summary" 
              icon={<WarningIcon />} 
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab 
              label="Potential Legal Actions" 
              icon={<GavelIcon />} 
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>

          {/* Tab 1: Breach Summary */}
          {resultsTab === 0 && (
            <Box sx={{ p: 3 }}>
              {breachData && breachData.breaches && breachData.breaches.length > 0 ? (
                <>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <strong>Found {breachData.breaches.length} breach(es)</strong> associated with your identifier.
                  </Alert>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {breachData.breaches.map((breach, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
                              {breach.source || 'Unknown Source'}
                            </Typography>
                            <Chip 
                              label={breach.date || 'Unknown Date'} 
                              size="small" 
                              color="error" 
                              variant="outlined" 
                            />
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Compromised Data:</strong>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Array.isArray(breach.data) && breach.data.length > 0 ? (
                              breach.data.map((dataType, i) => (
                                <Chip 
                                  key={i} 
                                  label={dataType} 
                                  size="small" 
                                  color="warning" 
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Information not available
                              </Typography>
                            )}
                          </Box>

                          {breach.details && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                              {breach.details}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Data source: {breachData.source || 'Free Tier APIs'}
                  </Typography>
                </>
              ) : (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  <strong>Good news!</strong> No breaches found for this identifier in our free database.
                </Alert>
              )}
            </Box>
          )}

          {/* Tab 2: Potential Legal Actions */}
          {resultsTab === 1 && (
            <Box sx={{ p: 3 }}>
              {lawsuitData && lawsuitData.loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                  <CircularProgress size={60} />
                  <Typography variant="h6" color="text.secondary">
                    Searching for related lawsuits...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This may take 15-30 seconds as we search ClassAction.org
                  </Typography>
                </Box>
              ) : lawsuitData && lawsuitData.lawsuits && lawsuitData.lawsuits.length > 0 ? (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>Search for class-action lawsuits</strong> related to {lawsuitData.lawsuits.length} breached {lawsuitData.lawsuits.length === 1 ? 'company' : 'companies'}.
                    Click the links below to search ClassAction.org for active lawsuits.
                  </Alert>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Due to legal restrictions, we cannot automatically scrape lawsuit results. Instead, we provide direct search links for each breached company.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {lawsuitData.lawsuits.map((lawsuit, index) => (
                      <Card key={index} variant="outlined" sx={{ '&:hover': { borderColor: 'primary.main', boxShadow: 2 }, transition: 'all 0.2s' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <GavelIcon color="primary" />
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                {lawsuit.company || lawsuit.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Search ClassAction.org for lawsuits related to the <strong>{lawsuit.company}</strong> data breach
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            href={lawsuit.search_url || lawsuit.link || `https://www.classaction.org/search?q=${encodeURIComponent(lawsuit.company || '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<SearchIcon />}
                            sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                          >
                            Search Lawsuits
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  <Alert severity="warning" sx={{ mt: 3 }}>
                    <strong>Disclaimer:</strong> This information is provided for educational purposes only.
                    Lawsuit information should be verified on ClassAction.org. This is not legal advice.
                    Consult with a qualified attorney to understand your legal rights and options.
                  </Alert>

                  {lawsuitData.legal_note && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {lawsuitData.legal_note}
                    </Typography>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  <strong>No lawsuit search links available.</strong> No breached companies were found to search for.
                  If you believe you may have legal options, consult with an attorney for personalized advice.
                </Alert>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default BreachCheck;
